/**
 * Comprehensive Doctor Management Dashboard
 * Provides access to all doctor management features
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Person as ProfileIcon,
  Schedule as AvailabilityIcon,
  Event as ScheduleIcon,
  People as PatientsIcon,
  Notes as NotesIcon,
  LocalPharmacy as PrescriptionIcon,
  Receipt as BillingIcon,
  Analytics as AnalyticsIcon,
  VerifiedUser as VerificationIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  DateRange as DateIcon,
  LocalHospital as HospitalIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  FileUpload as UploadFileIcon,
  FileDownload as DownloadFileIcon,
  FolderOpen as FolderOpenIcon,
  CreateNewFolder as CreateFolderIcon,
  DriveFileMove as MoveIcon,
  DriveFileRenameOutline as RenameIcon,
  ContentCopy as CopyIcon,
  Archive as ArchiveIcon,
  RestoreFromTrash as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Share as ShareIcon,
  Notifications as NotificationIcon,
  Description as DocumentIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  Favorite as HeartIcon,
  Sports as SportsIcon,
  Restaurant as FoodIcon,
  SmokingRooms as SmokingIcon,
  LocalDrink as DrinkIcon,
  Bedtime as SleepIcon,
  FitnessCenter as FitnessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// API Services
import {
  doctorProfileApi,
  doctorAvailabilityApi,
  doctorPatientApi,
  doctorConsultationApi,
  doctorPrescriptionApi,
  doctorBillingApi,
  doctorAnalyticsApi,
  doctorVerificationApi
} from '../../services/api/doctorManagement';

const DoctorManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Data states
  const [profileData, setProfileData] = useState(null);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [patientsData, setPatientsData] = useState([]);
  const [consultationsData, setConsultationsData] = useState([]);
  const [prescriptionsData, setPrescriptionsData] = useState([]);
  const [billingData, setBillingData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  
  // Dialog states
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showAvailabilityDialog, setShowAvailabilityDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);

  // Form states
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    gender: '',
    age: '',
    city: '',
    state: '',
    country: '',
    doctor_type: '',
    experience: '',
    consultation_fee: '',
    follow_up_fee: '',
    professional_summary: '',
    website: '',
    address: ''
  });

  const [availabilityForm, setAvailabilityForm] = useState({
    date: '',
    start_time: '',
    end_time: '',
    max_appointments: 10
  });

  const [verificationForm, setVerificationForm] = useState({
    mci_registration_number: '',
    rci_registration_number: '',
    insurance_provider: '',
    insurance_policy_number: '',
    clinic_registration_number: ''
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all doctor data in parallel
      const [
        profileResponse,
        availabilityResponse,
        patientsResponse,
        consultationsResponse,
        prescriptionsResponse,
        billingResponse,
        analyticsResponse,
        verificationResponse
      ] = await Promise.allSettled([
        doctorProfileApi.getProfile(),
        doctorAvailabilityApi.getAvailability(),
        doctorPatientApi.getPatients(),
        doctorConsultationApi.getConsultations(),
        doctorPrescriptionApi.getPrescriptions(),
        doctorBillingApi.getBilling(),
        doctorAnalyticsApi.getAnalytics(),
        doctorVerificationApi.getVerificationStatus()
      ]);

      // Set data from successful responses
      if (profileResponse.status === 'fulfilled') {
        setProfileData(profileResponse.value);
        setProfileForm(profileResponse.value);
      }
      if (availabilityResponse.status === 'fulfilled') {
        setAvailabilityData(availabilityResponse.value);
      }
      if (patientsResponse.status === 'fulfilled') {
        setPatientsData(patientsResponse.value);
      }
      if (consultationsResponse.status === 'fulfilled') {
        setConsultationsData(consultationsResponse.value);
      }
      if (prescriptionsResponse.status === 'fulfilled') {
        setPrescriptionsData(prescriptionsResponse.value);
      }
      if (billingResponse.status === 'fulfilled') {
        setBillingData(billingResponse.value);
      }
      if (analyticsResponse.status === 'fulfilled') {
        setAnalyticsData(analyticsResponse.value);
      }
      if (verificationResponse.status === 'fulfilled') {
        setVerificationData(verificationResponse.value);
        setVerificationForm(verificationResponse.value);
      }

    } catch (err) {
      console.error('Error loading doctor data:', err);
      setError('Failed to load doctor data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setLoading(true);
      await doctorProfileApi.updateProfile(profileForm);
      toast.success('Profile updated successfully!');
      setShowProfileDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAvailabilityUpdate = async () => {
    try {
      setLoading(true);
      await doctorAvailabilityApi.setAvailability(availabilityForm);
      toast.success('Availability updated successfully!');
      setShowAvailabilityDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error updating availability:', err);
      toast.error('Failed to update availability. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async () => {
    try {
      setLoading(true);
      await doctorVerificationApi.submitVerification(verificationForm);
      toast.success('Verification documents submitted successfully!');
      setShowVerificationDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error submitting verification:', err);
      toast.error('Failed to submit verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCompletionPercentage = () => {
    if (!profileData) return 0;
    
    const requiredFields = [
      'name', 'phone', 'gender', 'city', 'state', 'doctor_type',
      'experience', 'consultation_fee', 'professional_summary'
    ];
    
    const completedFields = requiredFields.filter(field => profileData[field]).length;
    return Math.round((completedFields / requiredFields.length) * 100);
  };

  const getVerificationStatus = () => {
    if (!verificationData) return 'pending';
    return verificationData.verification_status || 'pending';
  };

  const getVerificationColor = (status) => {
    switch (status) {
      case 'verified': return 'success';
      case 'in_review': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const StatCard = ({ title, value, icon, color, onClick, loading, trend, trendValue }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-4px)' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {loading ? <CircularProgress size={20} /> : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {trend === 'up' ? (
              <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
            ) : (
              <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
            )}
            <Typography variant="caption" color={trend === 'up' ? 'success.main' : 'error.main'}>
              {trendValue}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const FeatureCard = ({ title, description, icon, color, onClick, status, count }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          {count !== undefined && (
            <Badge badgeContent={count} color="primary">
              <Chip 
                label={status} 
                size="small" 
                color={status === 'Active' ? 'success' : 'default'}
              />
            </Badge>
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Button size="small" onClick={onClick}>
          Manage
        </Button>
      </CardActions>
    </Card>
  );

  if (loading && !profileData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Doctor Management Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Profile Completion Status */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Profile Completion</Typography>
          <Chip 
            label={`${getCompletionPercentage()}% Complete`} 
            color={getCompletionPercentage() === 100 ? 'success' : 'primary'}
            variant="outlined"
          />
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={getCompletionPercentage()} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Paper>

      {/* Verification Status */}
      {verificationData && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Verification Status</Typography>
            <Chip 
              label={getVerificationStatus().toUpperCase()} 
              color={getVerificationColor(getVerificationStatus())}
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Progress: {verificationData.verification_progress || 0}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={verificationData.verification_progress || 0} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Paper>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Patients"
            value={analyticsData?.total_patients || 0}
            icon={<PatientsIcon />}
            color="primary.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Appointments"
            value={analyticsData?.total_appointments || 0}
            icon={<ScheduleIcon />}
            color="secondary.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Revenue"
            value={`$${analyticsData?.monthly_revenue || 0}`}
            icon={<BillingIcon />}
            color="success.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Average Rating"
            value={analyticsData?.average_rating || 0}
            icon={<AnalyticsIcon />}
            color="info.main"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Feature Management */}
      <Grid container spacing={3}>
        {/* Profile Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Profile Management"
            description="Manage your professional profile and credentials"
            icon={<ProfileIcon />}
            color="primary.main"
            onClick={() => setShowProfileDialog(true)}
            status={getCompletionPercentage() === 100 ? 'Complete' : 'Incomplete'}
          />
        </Grid>

        {/* Availability Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Availability Management"
            description="Set your working hours and availability schedule"
            icon={<AvailabilityIcon />}
            color="secondary.main"
            onClick={() => setShowAvailabilityDialog(true)}
            status={availabilityData.length > 0 ? 'Active' : 'Not Set'}
            count={availabilityData.length}
          />
        </Grid>

        {/* Patient Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Patient Management"
            description="View and manage your patient list and records"
            icon={<PatientsIcon />}
            color="info.main"
            onClick={() => navigate('/doctor-dashboard/patients')}
            status={patientsData.length > 0 ? 'Active' : 'Empty'}
            count={patientsData.length}
          />
        </Grid>

        {/* Consultation Notes */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Consultation Notes"
            description="Manage consultation notes and patient records"
            icon={<NotesIcon />}
            color="warning.main"
            onClick={() => navigate('/doctor-dashboard/consultations')}
            status={consultationsData.length > 0 ? 'Active' : 'Empty'}
            count={consultationsData.length}
          />
        </Grid>

        {/* Prescription Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Prescription Management"
            description="Create and manage patient prescriptions"
            icon={<PrescriptionIcon />}
            color="success.main"
            onClick={() => navigate('/doctor-dashboard/prescriptions')}
            status={prescriptionsData.length > 0 ? 'Active' : 'Empty'}
            count={prescriptionsData.length}
          />
        </Grid>

        {/* Billing Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Billing Management"
            description="View billing information and financial reports"
            icon={<BillingIcon />}
            color="error.main"
            onClick={() => navigate('/doctor-dashboard/billing')}
            status={billingData.length > 0 ? 'Active' : 'Empty'}
            count={billingData.length}
          />
        </Grid>

        {/* Analytics Dashboard */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Analytics Dashboard"
            description="View performance metrics and analytics"
            icon={<AnalyticsIcon />}
            color="primary.main"
            onClick={() => setShowAnalyticsDialog(true)}
            status="Available"
          />
        </Grid>

        {/* Verification System */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Verification System"
            description="Manage your professional verification status"
            icon={<VerificationIcon />}
            color="secondary.main"
            onClick={() => setShowVerificationDialog(true)}
            status={getVerificationStatus()}
          />
        </Grid>
      </Grid>

      {/* Profile Update Dialog */}
      <Dialog open={showProfileDialog} onClose={() => setShowProfileDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Update Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={profileForm.gender}
                  onChange={(e) => setProfileForm({ ...profileForm, gender: e.target.value })}
                  label="Gender"
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={profileForm.age}
                onChange={(e) => setProfileForm({ ...profileForm, age: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={profileForm.city}
                onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={profileForm.state}
                onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Experience (Years)"
                type="number"
                value={profileForm.experience}
                onChange={(e) => setProfileForm({ ...profileForm, experience: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Consultation Fee"
                type="number"
                value={profileForm.consultation_fee}
                onChange={(e) => setProfileForm({ ...profileForm, consultation_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Professional Summary"
                value={profileForm.professional_summary}
                onChange={(e) => setProfileForm({ ...profileForm, professional_summary: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProfileDialog(false)}>Cancel</Button>
          <Button onClick={handleProfileUpdate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Update Profile'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Dialog */}
      <Dialog open={showAvailabilityDialog} onClose={() => setShowAvailabilityDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Set Availability</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={availabilityForm.date}
                onChange={(e) => setAvailabilityForm({ ...availabilityForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Start Time"
                value={availabilityForm.start_time}
                onChange={(e) => setAvailabilityForm({ ...availabilityForm, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="End Time"
                value={availabilityForm.end_time}
                onChange={(e) => setAvailabilityForm({ ...availabilityForm, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Appointments"
                value={availabilityForm.max_appointments}
                onChange={(e) => setAvailabilityForm({ ...availabilityForm, max_appointments: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAvailabilityDialog(false)}>Cancel</Button>
          <Button onClick={handleAvailabilityUpdate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Set Availability'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verification Dialog */}
      <Dialog open={showVerificationDialog} onClose={() => setShowVerificationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Verification Documents</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="MCI Registration Number"
                value={verificationForm.mci_registration_number}
                onChange={(e) => setVerificationForm({ ...verificationForm, mci_registration_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RCI Registration Number"
                value={verificationForm.rci_registration_number}
                onChange={(e) => setVerificationForm({ ...verificationForm, rci_registration_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Provider"
                value={verificationForm.insurance_provider}
                onChange={(e) => setVerificationForm({ ...verificationForm, insurance_provider: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Policy Number"
                value={verificationForm.insurance_policy_number}
                onChange={(e) => setVerificationForm({ ...verificationForm, insurance_policy_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Clinic Registration Number"
                value={verificationForm.clinic_registration_number}
                onChange={(e) => setVerificationForm({ ...verificationForm, clinic_registration_number: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVerificationDialog(false)}>Cancel</Button>
          <Button onClick={handleVerificationSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Submit Verification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={showAnalyticsDialog} onClose={() => setShowAnalyticsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Analytics Dashboard</DialogTitle>
        <DialogContent>
          {analyticsData && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Appointments"
                  value={analyticsData.total_appointments}
                  icon={<ScheduleIcon />}
                  color="primary.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Patients"
                  value={analyticsData.total_patients}
                  icon={<PatientsIcon />}
                  color="secondary.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Monthly Revenue"
                  value={`$${analyticsData.monthly_revenue}`}
                  icon={<BillingIcon />}
                  color="success.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Average Rating"
                  value={analyticsData.average_rating}
                  icon={<AnalyticsIcon />}
                  color="info.main"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAnalyticsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorManagement;
